import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Interactive DAG canvas (ADR-0089 update, 2026-07-02). Node cards are
// server-rendered divs (see runbooks/_graph.html.erb) -- this controller only
// ever mutates style.left/top and draws SVG edge lines; it never builds HTML
// (WIN convention, see stimulus.md). Persisted state changes (a new edge)
// go through a real Turbo Stream response instead of local DOM patching, so
// the server stays the single source of truth for the graph.
//
// Modeled on TxTavern's adventure_graph_controller.js, deliberately WITHOUT
// dagre or lodash.debounce (ADR-0089): Runbook::GraphLayout already supplies
// each node's default position server-side, and a five-line inline debounce
// replaces lodash for the one call site that needs it.
export default class extends Controller {
  static targets = ["canvas", "edgesSvg", "node", "connectHandle", "edgeDelete", "conditionLabel"]
  static values = {
    edges: Array,
    createEdgeUrl: String,
    createNodeUrl: String,
    nodeWidth: Number,
    nodeHeight: Number,
    // Glance-view gate (ADR-0089 addendum, 2026-07-02 round 2). runbooks#show
    // renders this partial with readonly: true; runbooks#build (the
    // interactive canvas) renders it readonly: false. When true, none of
    // drag/connect/click-to-edit/edge-delete are wired up -- the view partial
    // already omits the connect handles and delete markers in this mode, so
    // this is belt-and-braces against a stray target slipping through.
    readonly: Boolean
  }

  connect() {
    this._positions = {}
    this.nodeTargets.forEach(el => {
      this._positions[el.dataset.nodeId] = {
        x: parseFloat(el.style.left) || 0,
        y: parseFloat(el.style.top) || 0
      }
    })

    // Built once here, not per _drawEdges() call -- _drawEdges() fires on
    // every drag pointermove, and rebuilding a <marker> subtree that often
    // is wasted work for a static arrowhead definition.
    this._ensureArrowMarker()

    if (this.readonlyValue) return

    this._savePosition = this._debounce((url, x, y) => this._persistPosition(url, x, y), 400)
    this._onConnectMove = e => this._connectMove(e)
    this._onConnectEnd  = e => this._connectEnd(e)

    this.nodeTargets.forEach(el => el.addEventListener("pointerdown", e => this._nodeDragStart(e, el)))
    this.connectHandleTargets.forEach(handle => {
      handle.addEventListener("pointerdown", e => {
        e.preventDefault()
        e.stopPropagation()
        this._startConnect(handle.closest('[data-runbook-graph-target="node"]'))
      })
    })
    this.edgeDeleteTargets.forEach(marker => {
      marker.addEventListener("pointerdown", e => e.stopPropagation())
      marker.addEventListener("click", e => {
        e.stopPropagation()
        this._deleteEdge(marker.dataset.deleteUrl)
      })
    })

    // Right-click context menu (card #2156 Part C) -- ONE listener on the
    // controller's own element (the canvas wrapper), not per-node/per-marker.
    // openContextMenu inspects event.target to decide node-menu vs
    // empty-canvas-menu, mirroring the single-method approach requested for
    // this build (deliberately NOT the deck editor's 3-way
    // openContextMenu/openSlideContextMenu/openSlideRowContextMenu split --
    // that's flagged debt, not a pattern to repeat).
    this._onContextMenu = e => this.openContextMenu(e)
    this.element.addEventListener("contextmenu", this._onContextMenu)
  }

  disconnect() {
    this._cleanupNodeDrag?.()
    this._cleanupConnect()
    this.closeContextMenu()
    if (this._onContextMenu) this.element.removeEventListener("contextmenu", this._onContextMenu)
  }

  // --- Node drag + debounced position persist ---

  _nodeDragStart(e, el) {
    if (e.button !== 0) return
    e.preventDefault()

    const startX   = e.clientX
    const startY   = e.clientY
    const origLeft = parseFloat(el.style.left) || 0
    const origTop  = parseFloat(el.style.top) || 0
    let dragged = false

    const onMove = mv => {
      const dx = mv.clientX - startX
      const dy = mv.clientY - startY
      if (!dragged && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) dragged = true
      if (!dragged) return

      const newX = origLeft + dx
      const newY = origTop + dy
      el.style.left = `${newX}px`
      el.style.top  = `${newY}px`
      this._positions[el.dataset.nodeId] = { x: newX, y: newY }
      this._drawEdges()
    }

    const onUp = () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)

      // Click vs. drag, same small-movement threshold used above to flip
      // `dragged` -- a plain click (no movement past the threshold) opens
      // the node in the docked builder panel instead of persisting a
      // position no-op.
      //
      // sub_runbook exception (card #2156 Part B): if the node card carries
      // a data-navigate-url (its payload resolves to a real Definition -- see
      // runbooks/_graph.html.erb), a plain click navigates to that runbook's
      // OWN builder page in place instead of docking the edit panel, so
      // clicking through a sub_runbook node "feels like navigating through
      // the business". A sub_runbook node with no resolvable target (just
      // added from the palette, payload still empty) has no navigate-url and
      // falls through to the normal edit-panel behaviour below, since editing
      // the payload is the only way to set the target in the first place.
      if (!dragged) {
        if (el.dataset.navigateUrl) {
          Turbo.visit(el.dataset.navigateUrl)
        } else {
          this._openInPanel(el.dataset.editUrl)
        }
        return
      }

      const x = Math.round(parseFloat(el.style.left))
      const y = Math.round(parseFloat(el.style.top))
      this._savePosition(el.dataset.repositionUrl, x, y)
    }

    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    this._cleanupNodeDrag = () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
    }
  }

  async _persistPosition(url, x, y) {
    if (!url) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("runbook_node[position_x]", x)
    body.append("runbook_node[position_y]", y)

    try {
      await fetch(url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
        body
      })
    } catch (_error) {
      // Silently fail -- the node keeps its dragged-to position on screen;
      // the next successful drag (or a page reload) will reconcile it.
    }
  }

  // --- Click a node to dock its edit form (ADR-0089 addendum, round 2) ---
  //
  // The docked panel (turbo_frame_tag "runbook_builder_panel") lives outside
  // this controller's element -- it's in the builder page's right-hand rail,
  // not inside the canvas the controller is scoped to -- so it's reached by
  // id, the same way the CSRF meta tag is read above, rather than as a
  // Stimulus target/outlet.
  _openInPanel(url) {
    if (!url) return
    const frame = document.getElementById("runbook_builder_panel")
    if (frame) frame.src = url
  }

  // --- On-canvas edge delete (ADR-0089 addendum, round 2) ---

  async _deleteEdge(url) {
    if (!url) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/vnd.turbo-stream.html" }
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (_error) {
      // Silently fail -- the edge stays on canvas; a page reload will reconcile it.
    }
  }

  // --- Right-click context menu (card #2156 Part C) ---
  //
  // Single entry point, branching on event.target: a node card (or anything
  // inside one, found via closest()) gets the node menu, anything else on
  // the canvas gets the empty-canvas "add node" menu. An on-canvas edge's
  // delete marker keeps its existing click-to-delete interaction (× at the
  // midpoint) rather than gaining a redundant right-click menu of its own.
  //
  // The menu itself is a JS-built plain <div> appended to document.body,
  // positioned `fixed` at the cursor -- the same documented canvas-editor
  // chrome carve-out deck_editor_controller.js uses for its own context
  // menu (buildContextMenu/presentContextMenu/closeContextMenu), reused
  // here as a smaller, single-purpose version rather than importing that
  // controller's menu code wholesale.
  openContextMenu(event) {
    event.preventDefault()
    this.closeContextMenu()

    const nodeEl = event.target.closest('[data-runbook-graph-target="node"]')
    const items = nodeEl ? this._nodeContextItems(nodeEl) : this._canvasContextItems(event)
    if (!items.length) return

    this._presentContextMenu(this._buildContextMenu(items), event.clientX, event.clientY)
  }

  _canvasContextItems(event) {
    if (!this.hasCanvasTarget) return []

    // Canvas-local coordinates -- same conversion _connectMove uses for the
    // live drag-to-connect preview line, so a node created here lands
    // exactly under the cursor, not offset by the canvas's own scroll/inset.
    const rect = this.canvasTarget.getBoundingClientRect()
    const x = Math.round(event.clientX - rect.left)
    const y = Math.round(event.clientY - rect.top)

    return [
      { label: "Add trigger node",     action: () => this._createNodeAt("trigger", x, y) },
      { label: "Add tool node",        action: () => this._createNodeAt("tool", x, y) },
      { label: "Add approval node",    action: () => this._createNodeAt("approval", x, y) },
      { label: "Add sub-runbook node", action: () => this._createNodeAt("sub_runbook", x, y) },
      { label: "Add article node",     action: () => this._createNodeAt("article", x, y) },
      { label: "Add conditional node", action: () => this._createNodeAt("conditional", x, y) }
    ]
  }

  _nodeContextItems(nodeEl) {
    const items = [
      { label: "Edit", action: () => this._openInPanel(nodeEl.dataset.editUrl) }
    ]

    // sub_runbook only, and only once its payload actually resolves --
    // matches the plain-click navigation gate in _nodeDragStart's onUp.
    if (nodeEl.dataset.navigateUrl) {
      items.push({ label: "Open runbook →", action: () => Turbo.visit(nodeEl.dataset.navigateUrl) })
    }

    // Delegation picker (card #2156, ADR-0089 addendum) -- docks in the SAME
    // panel as Edit, via the same _openInPanel src= convention. Who's
    // responsible for this node: a human User, an AI persona (Sky/FIDO are
    // plain User rows), or nobody (rendered as a lightning bolt on the card).
    // Card #2880: same docked panel now also carries role-based delegation
    // and reference-article attach/detach, alongside the original per-user
    // assignment picker -- label/URL unchanged (an existing system test,
    // runbook_build_test.rb, asserts on this exact "Assign..." text), the
    // panel's own section headings communicate the expanded scope instead.
    items.push({ label: "Assign...", action: () => this._openInPanel(nodeEl.dataset.assignUrl) })

    items.push({ label: "Remove", danger: true, action: () => this._removeNode(nodeEl) })
    return items
  }

  async _createNodeAt(kind, x, y) {
    if (!this.hasCreateNodeUrlValue) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("runbook_node[kind]", kind)
    body.append("runbook_node[position_x]", x)
    body.append("runbook_node[position_y]", y)

    try {
      const response = await fetch(this.createNodeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/vnd.turbo-stream.html" },
        body
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (_error) {
      // Silently fail -- matches _createEdge's convention; the palette
      // remains available as a fallback add-node path.
    }
  }

  async _removeNode(nodeEl) {
    const url = nodeEl.dataset.destroyUrl
    if (!url) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/vnd.turbo-stream.html" }
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (_error) {
      // Silently fail -- matches _deleteEdge's convention; a page reload
      // will reconcile if the request never landed.
    }
  }

  // Build the positioned menu panel from a declarative items array
  // ({label, action, danger}). Danger items (Remove) are styled
  // text-red-600/hover:bg-red-50 and expected last, matching the deck
  // editor's context menu convention.
  _buildContextMenu(items) {
    const menu = document.createElement("div")
    menu.className = "fixed z-[60] min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 text-sm shadow-xl"

    items.forEach(item => {
      const button = document.createElement("button")
      button.type = "button"
      const tone = item.danger ? "text-red-600 hover:bg-red-50" : "text-gray-700 hover:bg-gray-50"
      button.className = `block w-full px-3 py-1.5 text-left ${tone}`
      button.textContent = item.label
      button.addEventListener("click", () => {
        this.closeContextMenu()
        item.action()
      })
      menu.appendChild(button)
    })

    return menu
  }

  _presentContextMenu(menu, clientX, clientY) {
    menu.style.left = `${clientX}px`
    menu.style.top = `${clientY}px`
    this._contextMenuEl = menu
    document.body.appendChild(menu)

    this._boundContextMenuDismiss = e => {
      if (this._contextMenuEl && !this._contextMenuEl.contains(e.target)) this.closeContextMenu()
    }
    this._boundContextMenuKeydown = e => {
      if (e.key === "Escape") this.closeContextMenu()
    }
    document.addEventListener("pointerdown", this._boundContextMenuDismiss, true)
    document.addEventListener("keydown", this._boundContextMenuKeydown)

    // Nudge back on-screen if the menu would overflow the viewport edges.
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect()
      if (rect.right > window.innerWidth) menu.style.left = `${Math.max(0, clientX - rect.width)}px`
      if (rect.bottom > window.innerHeight) menu.style.top = `${Math.max(0, clientY - rect.height)}px`
    })
  }

  closeContextMenu() {
    if (this._contextMenuEl) {
      this._contextMenuEl.remove()
      this._contextMenuEl = null
    }
    if (this._boundContextMenuDismiss) {
      document.removeEventListener("pointerdown", this._boundContextMenuDismiss, true)
      this._boundContextMenuDismiss = null
    }
    if (this._boundContextMenuKeydown) {
      document.removeEventListener("keydown", this._boundContextMenuKeydown)
      this._boundContextMenuKeydown = null
    }
  }

  // --- Drag-from-handle to create an edge ---

  _startConnect(fromEl) {
    if (!fromEl || !this.hasEdgesSvgTarget) return
    const fromId = fromEl.dataset.nodeId
    const pos = this._positions[fromId]
    if (!pos) return

    // fromKind (card #2279) -- carried through to _createEdge so it can
    // prompt for a branch value only when connecting FROM a conditional
    // node; every other kind's edges stay exactly as before (no prompt).
    this._connecting = { fromId, fromKind: fromEl.dataset.nodeKind }

    // Sapphire (--color-sapphire, #003d68) -- same token as the resting
    // connect-handle dot (bg-sapphire in the ERB partial), so the affordance
    // stays one colour through the whole drag-to-connect interaction.
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
    line.setAttribute("stroke", "#003d68")
    line.setAttribute("stroke-width", "1.5")
    line.setAttribute("stroke-dasharray", "5 3")
    const x1 = pos.x + this.nodeWidthValue
    const y1 = pos.y + this.nodeHeightValue / 2
    line.setAttribute("x1", x1)
    line.setAttribute("y1", y1)
    line.setAttribute("x2", x1)
    line.setAttribute("y2", y1)
    this.edgesSvgTarget.appendChild(line)
    this._tempLine = line

    document.addEventListener("pointermove", this._onConnectMove)
    document.addEventListener("pointerup", this._onConnectEnd)
  }

  _connectMove(e) {
    if (!this._connecting || !this._tempLine || !this.hasCanvasTarget) return

    const rect = this.canvasTarget.getBoundingClientRect()
    this._tempLine.setAttribute("x2", e.clientX - rect.left)
    this._tempLine.setAttribute("y2", e.clientY - rect.top)

    const target = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-runbook-graph-target="node"]')
    this.nodeTargets.forEach(n => {
      n.classList.toggle("ring-2", n === target && n.dataset.nodeId !== this._connecting.fromId)
      n.classList.toggle("ring-sapphire", n === target && n.dataset.nodeId !== this._connecting.fromId)
    })
  }

  _connectEnd(e) {
    if (!this._connecting) return
    const { fromId, fromKind } = this._connecting
    this._connecting = null

    document.removeEventListener("pointermove", this._onConnectMove)
    document.removeEventListener("pointerup", this._onConnectEnd)
    this._tempLine?.remove()
    this._tempLine = null
    this.nodeTargets.forEach(n => n.classList.remove("ring-2", "ring-sapphire"))

    const toEl = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-runbook-graph-target="node"]')
    const toId = toEl?.dataset.nodeId
    if (!toId || toId === fromId) return

    this._createEdge(fromId, toId, fromKind)
  }

  // fromKind (card #2279, MVP): connecting FROM a conditional node prompts
  // for the branch value with a plain window.prompt() -- no dropdown, no new
  // modal component, per the card's "keep this UI minimal" call. Cancelling
  // the prompt (null) aborts the whole connect instead of creating an
  // unlabelled edge a conditional node's Walker traversal would then fail on
  // at run time; an empty string is treated the same as "no value" (a
  // non-conditional-shaped edge) rather than saving a blank condition_value.
  async _createEdge(fromId, toId, fromKind) {
    if (!this.hasCreateEdgeUrlValue) return

    let conditionValue = null
    if (fromKind === "conditional") {
      conditionValue = window.prompt('Branch value for this edge (e.g. "true" or "false"):')
      if (conditionValue === null) return
      conditionValue = conditionValue.trim()
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("runbook_edge[from_node_id]", fromId)
    body.append("runbook_edge[to_node_id]", toId)
    if (conditionValue) body.append("runbook_edge[condition_value]", conditionValue)

    try {
      const response = await fetch(this.createEdgeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/vnd.turbo-stream.html" },
        body
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (_error) {
      // Silently fail -- the graph frame simply doesn't gain the new edge;
      // the off-canvas "+ Add edge" form remains available as a fallback.
    }
  }

  _cleanupConnect() {
    document.removeEventListener("pointermove", this._onConnectMove)
    document.removeEventListener("pointerup", this._onConnectEnd)
    this._tempLine?.remove()
    this._tempLine = null
    this._connecting = null
  }

  // --- Edge redraw (live drag preview only -- persisted edges are always
  // rendered server-side; this never creates or removes an edge, only moves
  // the lines already described by edgesValue) ---

  _drawEdges() {
    if (!this.hasEdgesSvgTarget) return
    // Only the lines are rebuilt on drag -- the marker def (appended once in
    // connect()) and any other static defs are left in place.
    this.edgesSvgTarget.querySelectorAll("line").forEach(el => el.remove())

    this.edgesValue.forEach(edge => {
      const from = this._positions[String(edge.from_node_id)]
      const to   = this._positions[String(edge.to_node_id)]
      if (!from || !to) return

      const { x1, y1, x2, y2 } = this._edgeAnchors(from, to)

      const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
      line.setAttribute("x1", x1)
      line.setAttribute("y1", y1)
      line.setAttribute("x2", x2)
      line.setAttribute("y2", y2)
      line.setAttribute("stroke", "#94a3b8")
      line.setAttribute("stroke-width", "1.5")
      line.setAttribute("marker-end", "url(#runbook-graph-arrow)")
      this.edgesSvgTarget.appendChild(line)
    })

    // The delete markers (<g data-runbook-graph-target="edgeDelete">) are
    // server-rendered, not rebuilt here like the lines above -- they just
    // need re-translating to the new midpoint so they track a dragged node
    // instead of going stale until the next server round-trip.
    this.edgeDeleteTargets.forEach(marker => {
      const from = this._positions[marker.dataset.fromId]
      const to   = this._positions[marker.dataset.toId]
      if (!from || !to) return

      const { x1, y1, x2, y2 } = this._edgeAnchors(from, to)
      marker.setAttribute("transform", `translate(${(x1 + x2) / 2}, ${(y1 + y2) / 2})`)
    })

    // Branch labels (card #2279) -- same re-translate-not-rebuild treatment
    // as the delete markers above, offset PERPENDICULAR to the edge (not a
    // flat vertical -14px, per Vela's review) so the label keeps clear of
    // the ×-marker sitting AT the exact midpoint regardless of edge angle --
    // must match runbooks/_graph.html.erb's server-rendered initial position.
    this.conditionLabelTargets.forEach(label => {
      const from = this._positions[label.dataset.fromId]
      const to   = this._positions[label.dataset.toId]
      if (!from || !to) return

      const { x1, y1, x2, y2 } = this._edgeAnchors(from, to)
      const midX = (x1 + x2) / 2
      const midY = (y1 + y2) / 2
      const dx = x2 - x1
      const dy = y2 - y1
      const length = Math.sqrt(dx * dx + dy * dy) || 1
      const labelX = midX + (-dy / length) * 14
      const labelY = midY + (dx / length) * 14
      label.setAttribute("transform", `translate(${labelX}, ${labelY})`)
    })
  }

  // Shortest-side edge anchoring (card #3072) -- given a FROM and TO node's
  // {x, y} top-left position, decides which side of each card an edge
  // exits/enters from (the near side, not always FROM's right / TO's left).
  // Compares centre-to-centre dx/dy: whichever axis has the larger absolute
  // delta decides the anchor pair (ties fall to horizontal, matching the
  // `>=` below). Deliberately duplicated (not shared) with the equivalent
  // Runbook::EdgeAnchor Ruby class used by runbooks/_graph.html.erb's
  // server-side initial render -- this codebase keeps ERB initial-render and
  // JS live-redraw math duplicated-but-synced, not shared across Ruby and JS.
  _edgeAnchors(from, to) {
    const w = this.nodeWidthValue
    const h = this.nodeHeightValue
    const dx = (to.x + w / 2) - (from.x + w / 2)
    const dy = (to.y + h / 2) - (from.y + h / 2)

    // Side-by-side cards (centres a full card width apart) always connect
    // horizontally -- keep in sync with Runbook::EdgeAnchor#call.
    if (Math.abs(dx) >= Math.abs(dy) || Math.abs(dx) >= w) {
      return dx >= 0
        ? { x1: from.x + w, y1: from.y + h / 2, x2: to.x, y2: to.y + h / 2 }
        : { x1: from.x, y1: from.y + h / 2, x2: to.x + w, y2: to.y + h / 2 }
    }

    return dy >= 0
      ? { x1: from.x + w / 2, y1: from.y + h, x2: to.x + w / 2, y2: to.y }
      : { x1: from.x + w / 2, y1: from.y, x2: to.x + w / 2, y2: to.y + h }
  }

  _ensureArrowMarker() {
    if (!this.hasEdgesSvgTarget || this.edgesSvgTarget.querySelector("#runbook-graph-arrow")) return

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
    const marker = document.createElementNS("http://www.w3.org/2000/svg", "marker")
    marker.setAttribute("id", "runbook-graph-arrow")
    marker.setAttribute("markerWidth", "10")
    marker.setAttribute("markerHeight", "10")
    marker.setAttribute("refX", "8")
    marker.setAttribute("refY", "3")
    marker.setAttribute("orient", "auto")
    marker.setAttribute("markerUnits", "strokeWidth")
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("d", "M0,0 L0,6 L9,3 z")
    path.setAttribute("fill", "#94a3b8")
    marker.appendChild(path)
    defs.appendChild(marker)
    this.edgesSvgTarget.appendChild(defs)
  }

  // --- Small inline debounce (ADR-0089: not worth adding lodash.debounce
  // for this one call site) ---

  _debounce(fn, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn(...args), wait)
    }
  }
}
